"use client";

import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@/store";
import { paymentSettled } from "@/store/slices/basketSlice";
import { assignmentApi } from "@/api/assignment/assignmentApi";
import { basketApi } from "@/api/basket/basketApi";
import { fetchPrepaidAccess, markPrepaidAccessGranted } from "@/lib/prepaidAccess";

/** Оплата брошена — перестаём ждать возврата с формы провайдера. */
const MAX_WAIT_MS = 30 * 60 * 1000;

/** Шаг и предел опроса доступа; дальше остаётся перепроверка при возврате на вкладку. */
const POLL_STEP_MS = 5 * 1000;
const POLL_UNTIL_MS = 5 * 60 * 1000;

/**
 * Пока идёт оплата (форма провайдера открыта в соседней вкладке), обновляет доступы
 * при возврате на эту вкладку. Штатно доступы обновляет событие OrderCompleted из
 * сокета, но оно приходит только в ту вкладку, где открыта корзина, и только если
 * сокет жив — иначе экзамен остаётся с замком до перезагрузки страницы.
 *
 * Возврата на вкладку тоже может не быть: Kaspi QR платят с телефона, вкладка на
 * компьютере всё это время остаётся видимой, visibilitychange не срабатывает. Поэтому,
 * когда известно оплачиваемое задание (basket.pendingAssignmentId), доступ дополнительно
 * опрашивается по таймеру — это и снимает замок без F5, и избавляет от второй оплаты.
 */
export function usePostPaymentRefresh() {
  const dispatch = useAppDispatch();
  const paymentStartedAt = useAppSelector((state) => state.basket.paymentStartedAt);
  const pendingAssignmentId = useAppSelector((state) => state.basket.pendingAssignmentId);

  useEffect(() => {
    if (!paymentStartedAt) return;

    const waited = Date.now() - paymentStartedAt;
    if (waited >= MAX_WAIT_MS) {
      dispatch(paymentSettled());
      return;
    }

    const revalidate = () => {
      if (document.visibilityState !== "visible") return;
      dispatch(assignmentApi.util.invalidateTags(["AssignmentsList", "AssignmentDetail"]));
      dispatch(basketApi.util.invalidateTags(["OrderTotal", "BasketItems"]));
    };

    document.addEventListener("visibilitychange", revalidate);
    const timeoutId = window.setTimeout(() => dispatch(paymentSettled()), MAX_WAIT_MS - waited);

    return () => {
      document.removeEventListener("visibilitychange", revalidate);
      window.clearTimeout(timeoutId);
    };
  }, [paymentStartedAt, dispatch]);

  useEffect(() => {
    if (!paymentStartedAt || !pendingAssignmentId) return;

    /* На медленной сети ответ не успевает прийти к следующему тику — без флага
       запросы копятся пачкой (школьный интернет, см. загрузку SDK на странице экзамена). */
    let isChecking = false;

    const intervalId = window.setInterval(async () => {
      if (isChecking) return;
      if (Date.now() - paymentStartedAt >= POLL_UNTIL_MS) {
        window.clearInterval(intervalId);
        return;
      }

      isChecking = true;
      try {
        // Доступ появился — дальше всё как после сокета: опрос гаснет, списки перечитываются
        if (await fetchPrepaidAccess(dispatch, pendingAssignmentId)) {
          markPrepaidAccessGranted(dispatch);
        }
      } finally {
        isChecking = false;
      }
    }, POLL_STEP_MS);

    return () => window.clearInterval(intervalId);
  }, [paymentStartedAt, pendingAssignmentId, dispatch]);
}
